<template>
  <template v-if="defaultExpanded || userRecipesCount > 1">
    <template v-if="!bulkActionsExpanded">
      <div ref="bulkActionsHintElem" class="control">
        <button
          id="bulk-actions-button"
          class="button is-info is-outlined"
          @click="bulkActionsExpanded = true"
        >
          <span class="icon-text">
            <span class="icon">
              <i class="fas fa-ellipsis-h"></i>
            </span>
            <span>Actions</span>
          </span>
        </button>
      </div>
    </template>

    <div
      class="is-inline-flex gap-2 is-flex-wrap-wrap"
      :class="bulkActionsExpanded ? '' : 'is-hidden'"
    >
      <BulkArchiveButtons
        v-if="enableEdit"
        :store="recipeSearchStore"
        entity-label="recipe"
        hint-event-name="recipe-bulk-selection:hint"
        @archive="handleArchive"
        @unarchive="handleUnarchive"
      >
        <template #delete>
          <BulkDeleteButton
            :store="recipeSearchStore"
            :visible-records="recipeSearchStore.visibleRecipes"
            entity-label="recipe"
            :delete-endpoint="bulkRecipeDeletionsUserPath(userId)"
            :note="deleteConfirms.bulk"
            @deleted="handleDeleted"
            @interrupted="refreshList"
          />
        </template>
      </BulkArchiveButtons>

      <BulkDataDownloadButton
        v-if="enableDataDownload"
        :selected-ids="recipeSearchStore.selection"
        :count="count"
        :user-id="userId"
        :daily-values-options="dailyValuesOptions"
        hint-event-name="recipe-bulk-selection:hint"
      />

      <template v-if="enableEdit">
        <BulkTagsButton
          :ids="recipeSearchStore.selection"
          :count="count"
          :tag-store="recipeTagStore"
          entity-label="recipe"
          hint-event-name="recipe-bulk-selection:hint"
          @applied="recipeSearchStore.loadInitial({ force: true })"
        />

        <BulkSetFacilityAllergensButton
          :selected-ids="recipeSearchStore.selection"
          :count="count"
          hint-event-name="recipe-bulk-selection:hint"
        />
      </template>

      <BulkCopyToAccountButton
        v-if="enableAdminCopy"
        :store="recipeSearchStore"
        :count="count"
        hint-event-name="recipe-bulk-selection:hint"
        @copied="handleCopied"
      />
    </div>
  </template>
</template>

<script setup>import { computed as _computed } from 'vue';

  import { watchEffect } from "vue";

  import { Flash } from "~/jose_modern/flash";
  import { Spinner } from "~/jose_modern/spinner";

  import BulkArchiveButtons from "../bulk-archive-buttons.vue";
  import BulkDeleteButton from "../bulk-delete-button.vue";
  import BulkTagsButton from "../bulk-tags-button.vue";
  import BulkDataDownloadButton from "./bulk-data-download-button.vue";
  import BulkSetFacilityAllergensButton from "./bulk-set-facility-allergens-button.vue";
  import BulkCopyToAccountButton from "./bulk-copy-to-account-button.vue";
  import {
    useAutoExpandActions,
    provideBulkActionsPanelGroup,
    DELETION_FLASH_DURATION,
  } from "../lib/use-bulk-actions.js";
  import { bulkRecipeDeletionsUserPath } from "~/routes";

  provideBulkActionsPanelGroup();

  const flashSuccess = (msg) => Flash.success(msg);
  const flashError = (msg) => Flash.error(msg);
  const startFullPageSpinner = () => Spinner.start(document.querySelector("body"));
  const stopFullPageSpinner = () => Spinner.stop(document.querySelector("body"));

  const props = defineProps({
    userId: { type: String, required: true },
    userRecipesCount: { type: Number, default: Infinity },

    defaultExpanded: { type: Boolean, default: false },

    enableEdit: { type: Boolean, default: false },
    enableDataDownload: { type: Boolean, default: true },
    enableAdminCopy: { type: Boolean, default: false },

    recipeSearchStore: { type: Object, required: true },
    recipeTagStore: { type: Object, required: true },

    // Pass down from Ruby: `"daily-values-options": daily_values_flat_json`; not needed here when enableDataDownload is false.
    dailyValuesOptions: { type: Array, required: true },

    deleteConfirms: { type: Object, required: true },
  });

  const count = _computed(() => props.recipeSearchStore.selection.length);

  const bulkActionsExpanded = useAutoExpandActions((count), { initial: props.defaultExpanded });

  const selectionArchiveState = _computed(() => props.recipeSearchStore.selectionArchiveState);
  const someArchivedSelected = _computed(() => selectionArchiveState.value.includes("archived"));
  const someUnarchivedSelected = _computed(() => selectionArchiveState.value.includes("unarchived"));
  const someUnknownSelected = _computed(() => selectionArchiveState.value.includes("unknown"));

  watchEffect(() => {
    const row = document.querySelector(".recipe-actions-row");
    if (row) row.toggleAttribute("data-bulk-expanded", bulkActionsExpanded.value);
  });

  function handleCopied(message) {
    flashSuccess(message);
    props.recipeSearchStore.clearSelection();
  }

  async function handleDeleted(message) {
    // The message can say nothing was deleted, so a success flash would contradict it.
    Flash.notice(message || "Recipes deleted.", { duration: DELETION_FLASH_DURATION });
    await refreshList();
  }

  async function refreshList() {
    props.recipeSearchStore.clearSelection();
    await props.recipeSearchStore.loadRecipePage(1);
  }
  async function handleArchive() {
    startFullPageSpinner();
    try {
      const successMessage = await props.recipeSearchStore.bulkUpdateRecipesArchived(true);
      if (successMessage) {
        flashSuccess(successMessage);
        await afterHandleBulkArchiveChange();
      }
    } catch (err) {
      console.error(err);
      if (typeof err === "string") {
        flashError(err);
      } else {
        flashError("Failed to update recipes");
      }
    }
    stopFullPageSpinner();
  }

  async function handleUnarchive() {
    startFullPageSpinner();
    try {
      const successMessage = await props.recipeSearchStore.bulkUpdateRecipesArchived(false);
      if (successMessage) {
        flashSuccess(successMessage);
        await afterHandleBulkArchiveChange();
      }
    } catch (err) {
      console.error(err);
      if (typeof err === "string") {
        flashError(err);
      } else {
        flashError("Failed to update recipes");
      }
    }
    stopFullPageSpinner();
  }

  async function afterHandleBulkArchiveChange() {
    if (props.recipeSearchStore.archived === null) {
      // If it's already in the mode where both archived and unarchived are shown, we just need to reload.
      await props.recipeSearchStore.loadInitial({ force: true });
      return;
    }

    // If they archived/unarchived we'll change the state of the filter so they still see the selected recipes.
    const setArchivedFilter = (v) => props.recipeSearchStore.setRecipeTypeFilter("archived", v);
    if (someArchivedSelected.value && !someUnarchivedSelected.value && !someUnknownSelected.value) {
      setArchivedFilter(true);
    } else if (someUnarchivedSelected.value && !someArchivedSelected.value && !someUnknownSelected.value) {
      setArchivedFilter(false);
    } else {
      // At time of writing, this case is unlikely to happen in practice, since Archive/Restore
      // have to succeed for all selected recipes (and otherwise we don't hit this code-path).
      // But this case could possibly come up if we changed to allowing partial success.
      setArchivedFilter(null);
    }
  }
</script>
